import React, { useState } from 'react';
import {
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useNavigate } from 'react-router-dom';
import ContextMenu from '../ui/ContextMenu';
import {
  collapseBreadcrumbSegments,
  type BreadcrumbSegment,
} from './collapseBreadcrumbSegments';

interface PageBreadcrumbProps {
  segments: BreadcrumbSegment[];
}

const segmentTextSx = {
  fontSize: 'inherit',
  lineHeight: 'inherit',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  // Buttons size to max-content; without this the wrapper clips them
  // with no ellipsis.
  maxWidth: '100%',
} as const;

/** One breadcrumb segment; shows the full label in a tooltip only when truncated. */
function SegmentItem({ segment }: { segment: BreadcrumbSegment }) {
  const navigate = useNavigate();
  const [isTruncated, setIsTruncated] = useState(false);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    const element = event.currentTarget;
    setIsTruncated(element.scrollWidth > element.clientWidth);
  };

  return (
    <Tooltip title={isTruncated ? segment.label : ''}>
      {segment.to ? (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          color="text.secondary"
          onClick={() => navigate(segment.to!)}
          onMouseEnter={handleMouseEnter}
          sx={segmentTextSx}
        >
          {segment.label}
        </Link>
      ) : (
        <Typography
          variant="body2"
          color="text.primary"
          onMouseEnter={handleMouseEnter}
          sx={segmentTextSx}
        >
          {segment.label}
        </Typography>
      )}
    </Tooltip>
  );
}

export const PageBreadcrumb: React.FC<PageBreadcrumbProps> = ({ segments }) => {
  const navigate = useNavigate();
  const [collapseAnchor, setCollapseAnchor] = useState<HTMLElement | null>(
    null
  );
  const { head, hidden, tail } = collapseBreadcrumbSegments(segments);

  return (
    <>
      <Breadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}
        aria-label="breadcrumb"
        sx={{
          typography: 'body2',
          minWidth: 0,
          overflow: 'hidden',
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
            alignItems: 'center',
          },
          '& .MuiBreadcrumbs-li': {
            // Segments shrink with ellipsis but never collapse entirely.
            minWidth: 28,
            flexShrink: 8,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          // The current segment gives up its space last when width runs
          // out. Keep the factor >= 1: a shrink-factor sum below 1 makes
          // flexbox leave part of the overflow unresolved.
          '& .MuiBreadcrumbs-li:last-of-type': {
            flexShrink: 1,
          },
          '& .MuiBreadcrumbs-li:has([data-testid="breadcrumb-collapse-button"])':
            {
              flexShrink: 0,
              minWidth: 'auto',
            },
          '& .MuiBreadcrumbs-separator': {
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          },
        }}
      >
        {head.map((segment, index) => (
          <SegmentItem key={`${index}-${segment.label}`} segment={segment} />
        ))}
        {hidden.length > 0 && (
          <IconButton
            size="small"
            aria-label="Show full path"
            aria-haspopup="menu"
            onClick={event => setCollapseAnchor(event.currentTarget)}
            data-testid="breadcrumb-collapse-button"
            sx={{ p: 0.25, borderRadius: 1 }}
          >
            <MoreHorizIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {tail.map((segment, index) => (
          <SegmentItem
            key={`tail-${index}-${segment.label}`}
            segment={segment}
          />
        ))}
      </Breadcrumbs>
      {hidden.length > 0 && (
        <ContextMenu
          open={Boolean(collapseAnchor)}
          onClose={() => setCollapseAnchor(null)}
          anchorEl={collapseAnchor}
          items={hidden.map((segment, index) => ({
            key: `breadcrumb-hidden-${index}`,
            label: segment.label,
            disabled: !segment.to,
            onClick: () => {
              if (segment.to) {
                navigate(segment.to);
              }
            },
          }))}
        />
      )}
    </>
  );
};
